import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Delete, Swap, Upload } from '@factorialco/f0-react/icons/app'
import {
  PagesFolderPagesListDocument,
  PagesFolderPagesListItemFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import {
  dateBackendDateTimeToRelativeTime,
  i18nDateToString,
  stringToDate,
} from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { usePagesContext } from 'modules/pages/lib/hooks/usePagesContext'

interface PagesListProps {
  handleCreatePage: () => void
  setOpenDeleteDialog: (id: number) => void
  setOpenMovePageModal: (id: number) => void
  folderSlug: string
  setOpenUploadFilesModal: (open: boolean) => void
}

export const PagesList = ({
  handleCreatePage,
  setOpenDeleteDialog,
  setOpenMovePageModal,
  folderSlug,
  setOpenUploadFilesModal,
}: PagesListProps) => {
  const { navigation } = useNavigation()
  const { canEditPages } = usePagesContext()

  const source = useGraphqlDataCollectionSource({
    document: PagesFolderPagesListDocument,
    mapData: (data) => data.handbookPages.pagesConnection.nodes,
    mapPagination: (data) => ({
      total: data.handbookPages.pagesConnection.totalCount ?? 0,
    }),
    totalItemSummary: (totalCount) =>
      totalCount === 1
        ? i18n.t('pages.pages_table_headers.total_item')
        : i18n.t('pages.pages_table_headers.total_items', { count: totalCount }),

    mapVariables: ({ search, sortings }) => ({
      search,
      folderSlug: folderSlug,
      sort: sortings?.[0],
    }),

    search: {
      enabled: true,
    },

    sortings: {
      title: {
        label: i18n.t('pages.pages_table_headers.title'),
        sortBy: 'title',
      },
      createdAt: {
        label: i18n.t('pages.pages_table_headers.created_at'),
        sortBy: 'createdAt',
      },
      updatedAt: {
        label: i18n.t('pages.pages_table_headers.updated_at'),
        sortBy: 'updatedAt',
      },
    },

    defaultSortings: {
      field: 'updatedAt',
      order: 'desc',
    },

    itemUrl: ({ id: pageId }) =>
      navigation.pages.folder.page.fullPath({
        folderSlug: folderSlug || '',
        pageId: String(pageId),
      }),

    primaryActions: canEditPages
      ? () => ({
          label: i18n.t('pages.folder.empty_state.new_page'),
          icon: Add,
          onClick: () => handleCreatePage(),
        })
      : undefined,

    secondaryActions: () =>
      canEditPages
        ? [
            {
              label: i18n.t('pages.pages_table_headers.import_pdf'),
              icon: Upload,
              onClick: () => setOpenUploadFilesModal(true),
            },
          ]
        : undefined,
    itemActions: canEditPages
      ? ({ id }) => [
          {
            label: i18n.t('pages.page.move_page.title'),
            icon: Swap,
            onClick: () => setOpenMovePageModal(id),
            hidden: !canEditPages,
          },
          {
            label: i18n.t('pages.page.delete'),
            icon: Delete,
            onClick: () => setOpenDeleteDialog(id),
            critical: true,
            hidden: !canEditPages,
          },
        ]
      : undefined,
  })

  const baseColumns = [
    {
      label: i18n.t('pages.pages_table_headers.title'),
      render: (item: PagesFolderPagesListItemFragment) => item.title || '-',
      sorting: 'title' as const,
    },
    {
      label: i18n.t('pages.pages_table_headers.created_by'),
      render: (item: PagesFolderPagesListItemFragment) => {
        return {
          type: 'person' as const,
          value: {
            firstName: item.owner?.firstName || '',
            lastName: item.owner?.lastName || '',
            src: buildUrl(item.owner?.avatar?.url),
          },
        }
      },
    },
    {
      label: i18n.t('pages.pages_table_headers.created_at'),
      render: (item: PagesFolderPagesListItemFragment) =>
        item.createdAt
          ? i18nDateToString(
              stringToDate(item.createdAt, 'backend_datetime'),
              'ui_date_day_month_year'
            )
          : '-',
      sorting: 'createdAt' as const,
    },
    {
      label: i18n.t('pages.pages_table_headers.updated_at'),
      render: (item: PagesFolderPagesListItemFragment) =>
        dateBackendDateTimeToRelativeTime(
          item.status === 'draft' ? item.updatedAt : item.lastPublishedAt
        ),
      sorting: 'updatedAt' as const,
    },
  ]

  const statusColumn = {
    label: i18n.t('pages.pages_table_headers.status'),
    render: (item: PagesFolderPagesListItemFragment) => {
      return {
        type: 'status' as const,
        value: {
          label:
            item.visibility === 'private' || item.status === 'draft'
              ? i18n.t('pages.page.draft')
              : i18n.t('pages.page.public'),
          status:
            item.visibility === 'private' || item.status === 'draft'
              ? ('neutral' as const)
              : ('info' as const),
        },
      }
    },
  }

  const columns = canEditPages ? [...baseColumns, statusColumn] : baseColumns

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns,
          },
        },
      ]}
    />
  )
}
